/* eslint-disable @typescript-eslint/unbound-method */
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/es/storage';

import {
  ComparisonItemType,
  ComparisonShiftDirectionEnum,
  ComparisonStateType,
} from './types';

export const comparisonAdapter = createEntityAdapter<ComparisonItemType>({
  sortComparer: (a, b) => a.addedBy - b.addedBy,
});

const comparisonInitialState: ComparisonStateType = {
  ...comparisonAdapter.getInitialState(),
  isShowDifference: false,
  isIrrelevant: false,
  selected: [],
};

export const comparisonSlice = createSlice({
  name: 'comparison',
  initialState: comparisonInitialState,
  reducers: {
    addItem: comparisonAdapter.addOne,
    removeItem: comparisonAdapter.removeOne,
    removeAllItems: comparisonAdapter.removeAll,
    actualizeItems: (state, action: PayloadAction<ComparisonItemType['id'][]>) => {
      const outdatedItems = state.ids.filter(id => !action.payload.includes(id));

      if (outdatedItems.length > 0) {
        comparisonAdapter.removeMany(state, outdatedItems);
        state.isIrrelevant = true;
      }
    },
    showDifferenceToggle: state => {
      state.isShowDifference = !state.isShowDifference;
    },
    setShowDifference: (
      state,
      action: PayloadAction<ComparisonStateType['isShowDifference']>,
    ) => {
      state.isShowDifference = action.payload;
    },
    setIrrelevant: (
      state,
      action: PayloadAction<ComparisonStateType['isIrrelevant']>,
    ) => {
      state.isIrrelevant = action.payload;
    },
    setSelected: (state, action: PayloadAction<ComparisonStateType['selected']>) => {
      state.selected = action.payload;
    },
    fillSelected: (state, action: PayloadAction<number>) => {
      const sortedIds = comparisonAdapter.getSelectors().selectIds(state);

      state.selected = [
        ...state.selected.filter(id => sortedIds.includes(id)),
        ...sortedIds.filter(id => !state.selected.includes(id)),
      ].slice(0, action.payload);
    },
    shiftAll: (
      state,
      action: PayloadAction<{ direction: ComparisonShiftDirectionEnum }>,
    ) => {
      const sortedIds = comparisonAdapter.getSelectors().selectIds(state);

      if (sortedIds.length < 1) {
        return;
      }

      if (!state.selected.length) {
        state.selected.push(sortedIds[0]);
      }

      for (let i = 0; i < state.selected.length; i++) {
        const currentIndex = sortedIds.indexOf(state.selected[i]);

        if (currentIndex === -1) {
          continue;
        }

        const loopedIds = [
          state.selected[i],
          ...sortedIds.slice(currentIndex + 1),
          ...sortedIds.slice(0, currentIndex),
        ];

        if (loopedIds.length < 1) {
          continue;
        }

        const newSelected =
          action.payload.direction === ComparisonShiftDirectionEnum.Right
            ? loopedIds[1]
            : loopedIds[loopedIds.length - 1];

        state.selected[i] = newSelected;
      }
    },
    shift: (
      state,
      action: PayloadAction<{
        itemId: ComparisonItemType['id'];
        direction: ComparisonShiftDirectionEnum;
      }>,
    ) => {
      const sortedIds = comparisonAdapter.getSelectors().selectIds(state);

      const selectedIndex = state.selected.indexOf(action.payload.itemId);
      const currentIndex = sortedIds.indexOf(action.payload.itemId);

      if (selectedIndex === -1 || currentIndex === -1) {
        return;
      }

      const loopedIds = [
        action.payload.itemId,
        ...sortedIds.slice(currentIndex + 1),
        ...sortedIds.slice(0, currentIndex),
      ].filter(id => id === action.payload.itemId || !state.selected.includes(id));

      if (loopedIds.length < 2) {
        return;
      }

      const newSelected =
        action.payload.direction === ComparisonShiftDirectionEnum.Right
          ? loopedIds[1]
          : loopedIds[loopedIds.length - 1];

      state.selected[selectedIndex] = newSelected;
    },
  },
});

export const comparisonSelectors = comparisonAdapter.getSelectors();

export default persistReducer(
  {
    key: 'comparison',
    version: 1,
    storage,
    blacklist: ['selected', 'isShowDifference', 'isIrrelevant'],
  },
  comparisonSlice.reducer,
);
